import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { ListUtil } from "src/framework/commons/ListUtil";
import { ValidateUtil } from "src/framework/commons/ValidateUtil";
import { InputCEP } from "src/framework/components/forms/inputs/InputCEP";
import api, { STATUS_CODE } from "src/framework/providers/api";
import { useObservable } from "src/framework/store/storeUtil";
import { cidades$, estados$ } from "src/store/global.store";
import { Box, IconButton } from "@mui/material";
import { Cidade } from "src/components/geral/RegioesDataTable/types";
import { TipoBateriaInput } from "./containers/TipoBateriaInput";
import { AutonomiaBateriaInput } from "./containers/AutonomiaBateriaInput";
import { TensaoSaidaInput } from "./containers/TensaoSaidaInput";
import { FormProjetoOffGridType } from "../../types";
import { TableViewIcon } from "./containers/TableViewIcon";
import { InputPrefix } from "./containers/InputPrefix";
import { elSemEstrutura } from "../../dimensionados_offgrid/containers/modalEditarEstruturas";
import { useProjetosOffGridStore } from "src/store/vendas/projetos/offgrid.store";
import { NOME_CLIENTE_MAX_LENGTH, POSICAO_MODULO } from "../../constants";
import { buscarEstruturasPorModulo } from "../../dimensionados_offgrid/utils/buscarEstruturasPorModulo";
import { notifierStore } from "src/framework/store/NotifierStore";
import { InputText } from "src/framework/components/forms/inputs/InputText";
import { InputCPFCNPJ } from "src/framework/components/forms/inputs/InputCPFCNPJ";
import { InputComboBoxList } from "src/framework/components/forms/inputs/InputComboBoxList";
import { parseCEP } from "src/framework/commons/masks";

interface FormClientProperties {
    errorState: string[];
    onChangeError: (errorState: any) => void
    formState: FormProjetoOffGridType
    onChangeValue: (formState: Partial<FormProjetoOffGridType>) => void
}

const CLIENTE_PROPOSTA = "cliente_proposta"
const EMAIL_PROPOSTA = "email_proposta"
const CIDADE_ID = "cidade_id"

export const FormClienteOffGrid: FC<FormClientProperties> = ({ errorState, onChangeError, formState, onChangeValue }) => {
    const { changeModalsValue } = useProjetosOffGridStore(store => store.actions)

    const projetosOffGridValidationsStoreState = useProjetosOffGridStore(store => store.state.validations);
    const { estrutura_selecionada } = useProjetosOffGridStore(store => store.state.data)
    const estadoListState = useObservable(estados$);
    const cidadeListState = useObservable(cidades$);

    const [estadoSelecionado, setEstadoSelecionado] = useState<string>("")

    const tipoEstruturasOptions = useMemo(()=>{
        return [elSemEstrutura, ...formState.estruturas]
    },[formState.estruturas])

    useEffect(()=>{
        if (formState?.estado) {
            setEstadoSelecionado(formState.estado.id)
        }
    }, [formState?.estado])

    const validateEmail = (email: string) => {
        return !!String(email)
          .toLowerCase()
          .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          );
    };

    const requestCep = useCallback(async(state: any) => {
        const formattedValue = state.cep.replace(/\D/g, "")
        if (formattedValue.length < 8) state.cep_encontrado = false
        if (formattedValue.length === 8) {
            const result = await api.get({
                url: `/cep/${formattedValue}`
            })
            
            if (result.statusCode !== STATUS_CODE.OK)   {
                state.estado = null
                state.cidade = null
                state.endereco_proposta = ''
                state.bairro = ''
                state.cep_encontrado = false
                state.cep = '';
                setEstadoSelecionado('');

                notifierStore.setNotifier({
                    title: "Aviso",
                    content: 'CEP não cadastrado. Preencha os campos do endereço manualmente.',
                    timeOut: 3500,
                    onClose: () => {
                        notifierStore.setOpen(false)
                    }
                })
                notifierStore.setOpen(true);
            } else {
                const { data } = result
                const estado = estadoListState?.data?.find((estado: any) => estado.id === data.uf)
                if (estado) {
                    state.cep_encontrado = true;

                    state.isTrocaUf = false;
                    state.tarifa_concessionaria_id = state.tarifa_concessionaria_id_original;
                    state.preco_kwh_com_imposto = state.preco_kwh_com_imposto_original;

                    if(estado.id !== state?.estado_original?.id){
                        state.isTrocaUf = true;
                        state.recalcular = true;
                    }
                }
                state.estado = estado
                let cidade = cidadeListState?.data?.find((cidade: Cidade) => cidade.id === data.ibge)
                if (!cidade && data) cidade = { id: data?.ibge, nome: data?.cidade, estado_id: data?.uf }

                if (cidade && state.cidade?.id !== cidade?.id) {
                    state.recalcular = true;
                }

                state.cidade = cidade
                state.bairro = data.bairro || ''
                state.endereco_proposta = data.endereco || ''
                state.numero = ''
                setEstadoSelecionado(data?.uf);
            }
        }
        onChangeValue(state)
    }, [cidadeListState, onChangeValue, estadoListState])

    return (
        <>
            <div className="row">
                <div className="X12 L6">
                    <InputText label="Título do projeto" fullWidth
                        value={formState?.titulo || ''}
                        defaultValue={formState?.titulo || ''}
                        maxLength={100}
                        onChange={(e: any) => {
                            onChangeValue({ titulo: e.target.value })
                        }}
                    />
                </div>
                <div className="X12 L6">
                    <InputText label="* Nome do cliente" fullWidth
                        value={formState?.cliente_proposta}
                        error={errorState.includes(CLIENTE_PROPOSTA)}
                        maxLength={NOME_CLIENTE_MAX_LENGTH}
                        defaultValue={formState?.cliente_proposta}
                        onChange={(e: any) => {
                            const state = { ...formState }
                            state.cliente_proposta = e.target.value
                            onChangeValue(state)
                        }}
                        onKeyUp={(e: any) => {
                            if (e.target.value !== "") {
                                const idxError = errorState.findIndex((es) => es === CLIENTE_PROPOSTA)
                                if (idxError > -1) {
                                    errorState.splice(idxError, 1)
                                    onChangeError(errorState)
                                }
                            }
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputText label="E-mail" fullWidth
                        value={formState?.email_proposta}
                        error={errorState.includes(EMAIL_PROPOSTA)}
                        helperText={errorState.includes(EMAIL_PROPOSTA) ? "E-mail inválido" : ''}
                        inputProps={{
                            autocomplete: 'off',
                        }}
                        onChange={(e: any) => {
                            const state = { ...formState }
                            state.email_proposta = (String(e.target.value) || '').trim()
                            onChangeValue(state)
                        }}
                        onKeyDown={(e: any) => {
                            const spaceRegex = /\s/g
                            e.target.value = e.target.value.replace(spaceRegex, '')
                        }}
                        onKeyUp={(e: any) => {
                            let _errorState = [...errorState]
                            if (e.target.value !== "") {
                                _errorState = ListUtil.removeItem(_errorState, EMAIL_PROPOSTA)
                                if (ValidateUtil.isValidEmail(e.target.value) && validateEmail(e.target.value)) {
                                    _errorState = ListUtil.removeItem(_errorState, EMAIL_PROPOSTA)
                                } else {
                                    _errorState = ListUtil.addItem(_errorState, EMAIL_PROPOSTA)
                                }
                            }else{
                                _errorState = ListUtil.removeItem(_errorState, EMAIL_PROPOSTA)
                            }
                            onChangeError(_errorState)
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputCPFCNPJ
                        label="CPF/CNPJ"
                        maxLength={18}
                        value={formState?.cpf_cnpj_proposta}
                        onChangeValue={(newValue) => {
                            const { value } = newValue;
                            onChangeValue({ cpf_cnpj_proposta: value })
                        }}
                    />
                </div>
                <div className="X12 L4">
                    <InputCEP
                        label="CEP"
                        value={formState?.cep || ''}
                        onChange={async(e: any) => {
                            const { value = '' } = e.target;
                            const state = { ...formState }
                            state.cep = parseCEP(value)
                            onChangeValue(state)
                            requestCep(state)

                            if (!value) {
                                state.cep = '';
                                state.endereco_proposta = '';
                                state.numero = '';
                                state.bairro = '';
                                onChangeValue(state)
                            }
                        }}
                        />
                </div>

                <div className="X12 L4">
                    <InputComboBoxList
                        id={`listagem-verticais-estado-cb`}
                        key={`listagem-verticais-estado-cb`}
                        label={"* UF"}
                        disabled={formState?.cep_encontrado}
                        textField="nome"
                        valueField="id"
                        options={estadoListState?.data || []}
                        value={formState?.estado || null}
                        autoComplete
                        renderOptions={(option: any) => {
                            return `${option.id} - ${option.nome}`
                        }}
                        renderOptionsLabel={(option: any) => {
                            return `${option.id} - ${option.nome}`
                        }}
                        onChange={(value: any) => {
                            const state = { ...formState };
                            if (value) {
                                state.isTrocaUf = false;

                                if(value.id !== state?.estado_original?.id){
                                    state.isTrocaUf = true;
                                    state.recalcular = true;
                                }

                                state.estado = value;
                                state.cidade = undefined;
                                state.cidade_id = undefined;

                                setEstadoSelecionado(String(value.id))

                            } else {
                                state.estado = null;
                                setEstadoSelecionado('')
                            }
                            onChangeValue(state)
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputComboBoxList
                        id={`listagem-verticais-cidade-cb`}
                        key={`listagem-verticais-cidade-cb`}
                        disabled={formState?.cep_encontrado || estadoSelecionado === ""}
                        label={"* Cidade"}
                        textField="nome"
                        valueField="id"
                        error={errorState.includes(CIDADE_ID)}
                        options={cidadeListState?.data?.filter((c: Cidade) => c.estado_id === estadoSelecionado) || []}
                        value={formState?.cidade || null}
                        autoComplete
                        renderOptions={(option: any) => {
                            return `${option?.nome}`
                        }}
                        renderOptionsLabel={(option: any) => {
                            return `${option?.nome}`
                        }}
                        onChange={(value: Cidade) => {
                            const state = { ...formState };
                            if (value) {
                                const idxError = errorState.findIndex((es) => es === CIDADE_ID)
                                if (idxError > -1) {
                                    errorState.splice(idxError, 1)
                                    onChangeError([...errorState])
                                }

                                if (state.cidade_id !== value?.id) {
                                    state.recalcular = true;
                                }

                                state.cidade = value;
                                state.cidade_id = value.id;
                            } else {
                                state.cidade = null;
                                state.cidade_id = undefined;
                            }

                            onChangeValue(state)
                        }}
                    />
                </div>

                <Box className="X12 L4" display={"flex"} alignItems="center">
                    <div className="X9 L10" style={{ position: "relative", padding: 0 }}>
                        <span onClick={() => changeModalsValue({ equipamentos: true })} style={{ cursor: 'pointer', display: "block", top: 0, left: 0, position: "absolute", zIndex: 10, width: "100%", height: "100%", backgroundColor: "transparent" }} />
                        <InputText
                            style={{ marginTop: "13px" }}
                            label="* Consumo diário total (kWh/dia)"
                            fullWidth
                            value={formState?.total_consumo_diario_kw  ? Intl.NumberFormat('pt-BR', { maximumFractionDigits: 3 }).format(formState?.total_consumo_diario_kw) : ''}
                            disabled
                        />
                    </div>
                    <div className="X3 L2">
                        <IconButton onClick={() => changeModalsValue({ equipamentos: true })} size="large">
                            <TableViewIcon />
                        </IconButton>
                    </div>
                </Box>

                <div className="X12 L4">
                    <TipoBateriaInput errorState={errorState} formState={formState} onChangeValue={onChangeValue} />
                </div>
                <div className="X12 L4">
                    <AutonomiaBateriaInput errorState={errorState} onChangeError={onChangeError} formState={formState} onChangeValue={onChangeValue} />
                </div>
                <div className="X12 L4">
                    <TensaoSaidaInput errorState={errorState} onChangeError={onChangeError} formState={formState} onChangeValue={onChangeValue} />
                </div>

                <div className="X12 L6">
                    <InputComboBoxList
                        id={"offgrid-modelo-modulo-field"}
                        key={`offgrid-modelo-modulo-field`}
                        label={"* Modelo do módulo"}
                        textField="nome"
                        disabled={!formState.estado?.id}
                        valueField="componente_id"
                        error={!projetosOffGridValidationsStoreState.modulo.status || errorState.includes("modulo_offgrid")}
                        options={formState.modulos || []}
                        value={formState.modulo_selecionado || null}
                        autoComplete
                        helperText={projetosOffGridValidationsStoreState.modulo.helper}
                        renderOptions={(option: any) => {
                            return `${option?.nome}`
                        }}
                        renderOptionsLabel={(option: any) => {
                            return `${option?.nome}`
                        }}
                        onChange={async (value) => {
                            const moduloSelecionado = formState?.modulo_selecionado;
                            const isTrocaModulo = value?.componente_id !== moduloSelecionado?.componente_id;
                            const payload: Partial<FormProjetoOffGridType> = { modulo_selecionado: value, isTrocaModulo }
                            const resultEstruturas = await buscarEstruturasPorModulo({
                                moduloId: value?.componente_id,
                                potencia: formState?.geracao_necessaria_kw,
                                regioesId: formState?.regioes || [],
                            })
                            if(resultEstruturas?.ativos?.length){
                                if(!resultEstruturas.ativos.find(item=> item.id === formState?.estrutura_selecionada?.id)){
                                    payload.estrutura_selecionada = null;
                                }
                            }
                            onChangeValue(payload)
                            //onChangeValue({ modulo_selecionado: value, recalcular: true, isTrocaModulo })
                        }}
                    />
                </div>

                <div className="X12 L6">
                    <InputComboBoxList
                        id={"offgrid-tipo-estrutura-field"}
                        key={`offgrid-tipo-estrutura-field`}
                        label={"* Tipo de estrutura"}
                        prefix={<InputPrefix />}
                        textField="nome"
                        valueField="id"
                        error={!projetosOffGridValidationsStoreState.estrutura.status || errorState.includes("tipo_estrutura_offgrid")}
                        options={tipoEstruturasOptions}
                        disabled={!formState.modulo_selecionado || !tipoEstruturasOptions.length}
                        helperText={projetosOffGridValidationsStoreState.estrutura.helper}
                        value={formState.estrutura_selecionada || null}
                        autoComplete
                        renderOptions={(option: any) => {
                            return `${option?.nome}`
                        }}
                        renderOptionsLabel={(option: any) => {
                            return `${option?.nome}`
                        }}
                        onChange={(value) => {
                            const updateState: Partial<FormProjetoOffGridType> = { estrutura_selecionada: value };
                            if(value?.id !== estrutura_selecionada?.id || value?.estruturas_grupo_id !== estrutura_selecionada?.estruturas_grupo_id){                          
                                updateState.fileiras = [{
                                    fileiras: 1,
                                    quantidade_modulos: 0,
                                    tipo_estrutura: { image: value?.image, text: value?.nome, value: value?.id, estruturas_grupo_id: value?.estruturas_grupo_id },
                                    posicao_modulo: POSICAO_MODULO.find(item => item.value === 'retrato') || null,
                                }];
                            }
                            onChangeValue(updateState);
                        }}
                    />
                </div>

                
            </div>
        </>
    );
}
